import * as React from 'react';
import { createContext, useState, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

interface AppContextType {
  tabNumber: number;
  setTabNumber: React.Dispatch<React.SetStateAction<number>>;
  setResetTab: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [resetTab, setResetTab] = useState(true);

  const pathname = location.pathname;
  const sessionKey = `tabNumber_${pathname}`;

  let tabNumber = parseInt(searchParams.get('tab') || '0', 10);
  if (!searchParams.has('tab')) {
    const savedTab = sessionStorage.getItem(sessionKey);
    if (savedTab) {
      tabNumber = parseInt(savedTab, 10);
    }
  } else {
    sessionStorage.setItem(sessionKey, tabNumber.toString());
  }

  const setTabNumber = useCallback((value: React.SetStateAction<number>) => {
    const newValue = typeof value === 'function' ? (value as (prevState: number) => number)(tabNumber) : value;
    sessionStorage.setItem(sessionKey, newValue.toString());
    setSearchParams((prevParams) => {
      if (newValue === 0) {
        prevParams.delete('tab');
      } else {
        prevParams.set('tab', newValue.toString());
      }
      return prevParams;
    }, { replace: true }); // replace: true giúp việc bấm Back trên trình duyệt không phải lùi lại từng tab
  }, [tabNumber, setSearchParams, sessionKey]);

  const contextValue = {
    tabNumber,
    setTabNumber: setTabNumber as any,
    setResetTab,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = React.useContext(AppContext);

  if (!context) {
    throw new Error('AppContext is not available');
  }

  return context;
};
